
import { defineComponent, ref, reactive, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonRepository = RepositoryFactory.get('season')
const SeasonGameRepository = RepositoryFactory.get('seasonGame')
const SeasonGameEventRepository = RepositoryFactory.get('seasonGameEvent')
const SeasonEntryRosterRepository = RepositoryFactory.get('seasonEntryRoster')

import BaseBreadcrumb, { Breadcrumb } from '@/components/BaseBreadcrumb.vue'
import BaseDialog from '@/components/BaseDialog.vue'
import ScoreBoard from '@/components/seasonGameScore/ScoreBoard.vue'

import { Dialog } from '@/types/dialog'
import { Season } from '@/types/season'
import { Game } from '@/types/game'
import { GameEvent } from '@/types/gameEvent'
import { GameEventForm } from '@/types/gameEventForm'
import { Custom } from '@/types/custom'
import { Group } from '@/types/group'
import { RosterPlayer } from '@/types/rosterPlayer'

export default defineComponent({
  name: 'SeasonGameScore',
  inheritAttrs: false,
  components: {
    BaseBreadcrumb,
    BaseDialog,
    ScoreBoard
  },
  setup() {
    const route = useRoute()
    const store = useStore()

    onMounted(() => {
      getSeason()
      getGameEvents()
    })

    const season: {
      data?: Season
    } = reactive({
      data: undefined
    })

    const groups: {
      customList: Custom[]
      custom?: Custom
      groupList: Group[]
      group?: Group
    } = reactive({
      customList: [],
      custom: undefined,
      groupList: [],
      group: undefined
    })

    const getSeason = async () => {
      try {
        const { season_id } = route.params

        const res = await SeasonRepository.getSeason(season_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          season.data = data.season
          groups.customList = data.groups

          getGame()
        }
      } catch (error) {
        //
      }
    }

    const game: {
      data?: Game
    } = reactive({
      data: undefined
    })

    const getGame = async () => {
      try {
        const { season_id, game_id } = route.params

        const res = await SeasonGameRepository.getGame(season_id, game_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          game.data = data.game
          if (game.data?.home.id != undefined && game.data?.away.id != undefined) {
            getHomeRosters(game.data?.home.id)
            getAwayRosters(game.data?.away.id)
          }

          const custom_id = game.data?.custome_id
          const group_id = game.data?.group_id

          groups.custom = groups.customList.find(custom => custom.id == custom_id)
          if (groups.custom == undefined) return

          groups.groupList = groups.custom.groups
          groups.group = groups.groupList.find(group => group.id == group_id)
        }
      } catch (error) {
        //
      }
    }

    const events: {
      home: GameEvent[]
      away: GameEvent[]

      selected?: GameEvent
    } = reactive({
      home: [],
      away: [],

      selected: undefined
    })

    const getGameEvents = async () => {
      try {
        const { season_id, game_id } = route.params

        const res = await SeasonGameEventRepository.getGameEvents(season_id, game_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          events.home = data.home_events
          events.away = data.away_events
        }
      } catch (error) {
        //
      }
    }

    const addGameEvent = async (event_id: number, teamType: string) => {
      try {
        const { season_id, game_id } = route.params

        const entry_team_id = teamType === 'home' ? game.data?.home?.id : game.data?.away?.id


        const dataObj: GameEventForm = {
          entry_team_id: entry_team_id as number,
          event_id: event_id,
          player_id: undefined,
          value: '',
          time: '',
          game_status: undefined
        }

        const res = await SeasonGameEventRepository.postGameEvent(season_id, game_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          if (teamType === 'home') {
            events.home.push(data.new)
          } else if (teamType === 'away') {
            events.away.push(data.new)
          }

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'イベントを追加しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const updateGameEvent = async (event: GameEvent) => {
      try {
        const { season_id, game_id } = route.params

        const dataObj = {
          game_event_id: event.id as number,
          player_id: event.player_id,
          value: event.value,
          time: event.time
        }

        const res = await SeasonGameEventRepository.updateGameEvent(season_id, game_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'イベントを更新しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const eventDialogDelete = reactive<Dialog>({
      title: '{イベント名}を削除しますか？',
      message: '削除したイベントは元に戻すことができません。',
      confirmText: '削除する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openEventDialogDelete = (event: GameEvent) => {
      events.selected = event
      eventDialogDelete.title = event.event_name + ' を削除しますか？'

      eventDialogDelete.isOpen = true
    }

    const closeEventDialogDelete = () => {
      eventDialogDelete.isOpen = false
    }

    const deleteGameEvent = async () => {
      try {
        const { season_id, game_id } = route.params

        const dataObj = {
          game_event_id: events.selected?.id
        }

        const res = await SeasonGameEventRepository.deleteGameEvent(season_id, game_id, dataObj)

        if (res.status === 200) {
          getGameEvents()

          closeEventDialogDelete()
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'イベントを削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const rosters: {
      home: RosterPlayer[]
      away: RosterPlayer[]
    } = reactive({
      home: [],
      away: []
    })

    const getHomeRosters = async (entry_team_id: number) => {
      try {
        const { season_id } = route.params

        const res = await SeasonEntryRosterRepository.getRosters(season_id, entry_team_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          rosters.home = data.rosters
        }
      } catch (error) {
        //
      }
    }

    const getAwayRosters = async (entry_team_id: number) => {
      try {
        const { season_id } = route.params

        const res = await SeasonEntryRosterRepository.getRosters(season_id, entry_team_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          rosters.away = data.rosters
        }
      } catch (error) {
        //
      }
    }

    const scoreSheetUrl = ref('https://ssbee.crew.bz/sheet/game/' + route.params.game_id)

    const scoreTab = reactive({
      id: 'home'
    })

    const changeScoreTab = (teamType: 'home' | 'away') => {
      scoreTab.id = teamType
    }

    onMounted(() => {
      mediaQuery.list = window.matchMedia('(max-width: 560px)')
      mediaQuery.list.addEventListener('change', setDeviceWidth)
      setDeviceWidth()
    })

    const mediaQuery: {
      list?: MediaQueryList
      isMobile: boolean
    } = reactive({
      list: undefined,
      isMobile: false
    })

    const setDeviceWidth = (): void => {
      if (mediaQuery.list?.matches) {
        mediaQuery.isMobile = true
      } else {
        mediaQuery.isMobile = false
      }
    }

    useHead({
      title: computed(() => '試合の流れ - ' + season.data?.topic_name + ' ' + season.data?.name)
    })

    const breadcrumbs = computed(() => {
      return [
        {
          type: 'link',
          link: {
            name: '大会一覧',
            path: '/'
          }
        },
        {
          type: 'link',
          size: 'large',
          link: {
            name: season.data?.topic_name,
            secondName: season.data?.name,
            path: '/event/' + route.params.topic_id + '/season/' + route.params.season_id,
          },
          setting: {
            path: '/event/' + route.params.topic_id
          }
        },
        {
          type: 'link',
          link: {
            name: groups.custom?.name,
            path: `/event/${route.params.topic_id}/season/${route.params.season_id}/game?custom=${groups.custom?.id}`,
          }
        },
        {
          type: 'text',
          text: {
            name: '試合の流れ'
          }
        }
      ] as Breadcrumb[]
    })

    return {
      season,
      groups,
      getSeason,

      game,
      getGame,

      events,
      getGameEvents,

      addGameEvent,
      updateGameEvent,

      eventDialogDelete,
      openEventDialogDelete,
      closeEventDialogDelete,
      deleteGameEvent,

      rosters,
      getHomeRosters,
      getAwayRosters,

      scoreSheetUrl,

      scoreTab,
      changeScoreTab,

      mediaQuery,

      breadcrumbs
    }
  }
})
